import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Sparkles, ArrowLeft } from "lucide-react";
import Loader from "./Loader";
import host from "../global";
import { useTheme } from "../hooks/useTheme";
import CodingProblemReviewForm from "./orgadmin/coding/wizard/CodingProblemReviewForm";

// ── Validation ────────────────────────────────────────────────────────────────
const blank = (s) => !s || s.trim().length === 0;

const validateTitle = (v) => {
  if (blank(v)) return "Title is required";
  if (v.trim().length < 3) return "Must be at least 3 characters";
  if (v.trim().length > 150) return "Must not exceed 150 characters";
  return "";
};

const validateDescription = (v) => {
  if (blank(v)) return "Description is required";
  if (v.trim().length < 20) return "Must be at least 20 characters";
  if (v.trim().length > 2000) return "Must not exceed 2000 characters";
  return "";
};

const validateFormat = (v, label) => {
  if (blank(v)) return `${label} is required`;
  if (v.trim().length > 500) return `Must not exceed 500 characters`;
  return "";
};

const validateExamples = (examples) =>
  examples.map((ex) => {
    const e = {};
    if (blank(ex.input)) e.input = "Required";
    if (blank(ex.output)) e.output = "Required";
    if (blank(ex.explanation)) e.explanation = "Required";
    else if (ex.explanation.trim().length < 10) e.explanation = "Min 10 characters";
    return Object.keys(e).length ? e : null;
  });

const validateTestCases = (cases) =>
  cases.map((tc) => {
    const e = {};
    if (blank(tc.input)) e.input = "Required";
    if (blank(tc.output)) e.output = "Required";
    return Object.keys(e).length ? e : null;
  });

// ── Char progress bar ─────────────────────────────────────────────────────────
const CharBar = ({ value, max, min }) => {
  const pct = Math.min((value / max) * 100, 100);
  const color =
    value < min
      ? "bg-gray-300 dark:bg-white/10"
      : value > max * 0.9
      ? "bg-rose-400"
      : value > max * 0.75
      ? "bg-amber-400"
      : "bg-emerald-400";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-0.5 rounded-full bg-gray-200 dark:bg-white/[0.06] overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-200 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={`text-[11px] tabular-nums ${
          value > max ? "text-rose-400" : "text-gray-400 dark:text-gray-500"
        }`}
      >
        {value}/{max}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const PHASE = { PROMPT: "prompt", GENERATING: "generating", REVIEW: "review" };
const PHASE_ORDER = [PHASE.PROMPT, PHASE.GENERATING, PHASE.REVIEW];
const PHASE_LABELS = ["Describe", "Generate", "Review"];

const GENERATE_STEPS = [
  "Reading your description",
  "Writing input/output format",
  "Generating examples",
  "Adding test cases",
  "Writing starter code",
];

const QUICK_PICKS = [
  { title: "Two Sum", desc: "Given an array of integers and a target, return indices of two numbers that add up to the target." },
  { title: "Reverse a Linked List", desc: "Given the head of a singly linked list, reverse the list and return it." },
  { title: "Fibonacci Number", desc: "Given n, return the nth Fibonacci number using the recurrence F(n) = F(n-1) + F(n-2)." },
  { title: "Valid Parentheses", desc: "Given a string of brackets, determine if the brackets are balanced and valid." },
  { title: "Binary Search", desc: "Search for a target in a sorted array and return its index, or -1 if not found." },
];

const HostCodingProblem = ({ formData, setFormData, handleSubmit: submitForm, setFormVisible }) => {
  const accessToken = sessionStorage.getItem("accessToken");
  const { theme } = useTheme();
  const [phase, setPhase] = useState(PHASE.PROMPT);
  const [promptTitle, setPromptTitle] = useState(formData.title || "");
  const [promptDesc, setPromptDesc] = useState(formData.description || "");
  const [promptErrors, setPromptErrors] = useState({});
  const [reviewErrors, setReviewErrors] = useState({});
  const [genStep, setGenStep] = useState(0);

  // Advance generating step list every 2.5s while generating
  useEffect(() => {
    if (phase !== PHASE.GENERATING) { setGenStep(0); return; }
    const id = setInterval(() => setGenStep((s) => Math.min(s + 1, GENERATE_STEPS.length - 1)), 2500);
    return () => clearInterval(id);
  }, [phase]);

  // ── Phase 1: generate ─────────────────────────────────────────────────────
  const handleGenerate = async () => {
    const errs = {};
    const te = validateTitle(promptTitle);
    const de = validateDescription(promptDesc);
    if (te) errs.title = te;
    if (de) errs.description = de;
    if (Object.keys(errs).length) { setPromptErrors(errs); return; }
    setPromptErrors({});

    setPhase(PHASE.GENERATING);
    try {
      const res = await axios.post(
        `${host}/api/generate_coding_problem`,
        { title: promptTitle.trim(), description: promptDesc.trim() },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setFormData({
        title: promptTitle.trim(),
        description: promptDesc.trim(),
        input_format: res.data.input_format ?? "",
        output_format: res.data.output_format ?? "",
        constraints: res.data.constraints ?? "",
        examples: res.data.examples?.length
          ? res.data.examples
          : [{ input: "", output: "", explanation: "" }],
        test_cases: res.data.test_cases?.length
          ? res.data.test_cases
          : [{ input: "", output: "" }],
        final_test_cases: res.data.final_test_cases?.length
          ? res.data.final_test_cases
          : [{ input: "", output: "" }],
        boilerplate_code: res.data.boilerplate_code ?? {},
      });
      setPhase(PHASE.REVIEW);
    } catch {
      setPhase(PHASE.PROMPT);
      setPromptErrors({ api: "Generation failed. Please try again." });
    }
  };

  // ── Phase 2: submit ───────────────────────────────────────────────────────
  const handleSave = () => {
    const errs = {};

    const te = validateTitle(formData.title);
    const de = validateDescription(formData.description);
    if (te) errs.title = te;
    if (de) errs.description = de;

    const ifErr = validateFormat(formData.input_format, "Input Format");
    const ofErr = validateFormat(formData.output_format, "Output Format");
    const cErr = validateFormat(formData.constraints, "Constraints");
    if (ifErr) errs.input_format = ifErr;
    if (ofErr) errs.output_format = ofErr;
    if (cErr) errs.constraints = cErr;

    const exErrs = validateExamples(formData.examples);
    if (exErrs.some(Boolean)) errs.examples = exErrs;

    const tcErrs = validateTestCases(formData.test_cases);
    const ftErrs = validateTestCases(formData.final_test_cases);
    if (tcErrs.some(Boolean)) errs.test_cases = tcErrs;
    if (ftErrs.some(Boolean)) errs.final_test_cases = ftErrs;

    setReviewErrors(errs);
    if (!Object.keys(errs).length) {
      submitForm({ preventDefault: () => {} });
    }
  };

  // ── Close guard ───────────────────────────────────────────────────────────
  const handleClose = () => {
    const dirty = promptTitle.trim() || promptDesc.trim();
    if (dirty && phase !== PHASE.GENERATING) {
      if (!window.confirm("Discard this problem? Unsaved changes will be lost.")) return;
    }
    setFormVisible(false);
  };

  const currentPhaseIdx = PHASE_ORDER.indexOf(phase);

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20"
        style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
        onClick={handleClose}
      />
      <div
        className="w-full max-w-2xl flex flex-col max-h-[90vh] relative bg-bgSecondary rounded-2xl shadow-2xl border border-white/10 animate-[fadeInScale_0.2s_ease-out]"
        style={{ background: theme === "dark" ? "linear-gradient(to bottom right, rgba(15,18,42,0.94), rgba(10,9,28,0.90))" : "" }}
      >

        {/* ── Phase stepper ── */}
        <div className="flex-shrink-0 flex items-center px-6 pt-4 pb-1">
          {PHASE_LABELS.map((label, i) => {
            const isDone = i < currentPhaseIdx;
            const isActive = i === currentPhaseIdx;
            return (
              <React.Fragment key={label}>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border transition-all duration-300 ${
                    isDone
                      ? "bg-[#069855]/10 border-[#069855] text-[#069855]"
                      : isActive
                      ? "border-[#069855] text-[#069855]"
                      : "border-gray-300 dark:border-white/20 text-gray-400 dark:text-gray-500"
                  }`}>
                    {isDone ? (
                      <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 5l2.5 2.5L8.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : i + 1}
                  </div>
                  <span className={`text-[11px] font-medium ${
                    isDone || isActive ? "text-[#069855]" : "text-gray-400 dark:text-gray-500"
                  }`}>{label}</span>
                </div>
                {i < 2 && (
                  <div className="flex-1 mx-2 h-px bg-gray-200 dark:bg-white/10 relative overflow-hidden" style={{ minWidth: 20 }}>
                    <div
                      className="absolute inset-y-0 left-0 bg-[#069855] transition-all duration-500"
                      style={{ width: i < currentPhaseIdx ? "100%" : "0%" }}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Header ── */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 pt-3 pb-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-semibold dark:text-white text-textPrimary" style={{ fontFamily: "Poppins, sans-serif" }}>
              {phase === PHASE.REVIEW ? "Review & Edit Problem" : "Host a Custom Problem"}
            </h2>
            <p className="text-xs dark:text-gray-400 text-textSecondary mt-0.5">
              {phase === PHASE.PROMPT && "AI builds the full problem from your description."}
              {phase === PHASE.GENERATING && "AI is generating your problem…"}
              {phase === PHASE.REVIEW && "Everything is editable. Adjust anything before saving."}
            </p>
          </div>
          {phase !== PHASE.GENERATING && (
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full flex items-center justify-center dark:text-gray-400 text-textSecondary hover:text-red-400 hover:bg-red-400/10 transition-all"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* PHASE 1: Prompt input */}
          {phase === PHASE.PROMPT && (
            <div className="space-y-5">

              {/* Enhanced AI info banner */}
              <div
                className="flex items-start gap-4 p-4 rounded-2xl border border-blue-200/50 dark:border-blue-500/20"
                style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.06), rgba(139,92,246,0.06), rgba(236,72,153,0.04))" }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))" }}
                >
                  <Sparkles size={18} className="text-blue-500 dark:text-blue-400" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-semibold dark:text-white text-gray-700">What happens next?</p>
                  <p className="text-xs dark:text-gray-400 text-gray-500 leading-relaxed">
                    You describe the problem → our AI writes the input/output format, constraints, examples,
                    test cases, and starter code → you review and tweak everything before saving.
                  </p>
                </div>
              </div>

              {/* Title */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold dark:text-white text-textPrimary">
                  Problem Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={promptTitle}
                  onChange={(e) => setPromptTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                  placeholder="e.g. Two Sum, Reverse a Linked List, Longest Palindrome…"
                  maxLength={150}
                  autoFocus
                  className={`w-full dark:glass-effect bg-bgPrimary px-4 py-3 rounded-xl dark:text-white text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 placeholder:text-textSecondary border border-gray-200 dark:border-white/[0.08] ${
                    promptErrors.title ? "ring-2 ring-red-400 border-red-400/40" : ""
                  }`}
                />
                <div className="space-y-1">
                  {promptErrors.title
                    ? <p className="text-red-400 text-xs">{promptErrors.title}</p>
                    : <p className="text-[11px] text-gray-400 dark:text-gray-500">Min 3 characters</p>
                  }
                  <CharBar value={promptTitle.length} max={150} min={3} />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold dark:text-white text-textPrimary">
                  Problem Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={promptDesc}
                  onChange={(e) => setPromptDesc(e.target.value)}
                  placeholder="Describe what the problem asks for. What's the input? What should be returned? Any special conditions? A few sentences is enough."
                  rows={5}
                  maxLength={2000}
                  className={`w-full dark:glass-effect bg-bgPrimary px-4 py-3 rounded-xl dark:text-white text-textPrimary text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 placeholder:text-textSecondary resize-none border border-gray-200 dark:border-white/[0.08] ${
                    promptErrors.description ? "ring-2 ring-red-400 border-red-400/40" : ""
                  }`}
                />
                <div className="space-y-1">
                  {promptErrors.description
                    ? <p className="text-red-400 text-xs">{promptErrors.description}</p>
                    : <p className="text-[11px] text-gray-400 dark:text-gray-500">Min 20 characters · No HTML tags</p>
                  }
                  <CharBar value={promptDesc.length} max={2000} min={20} />
                </div>
              </div>

              {/* Quick-pick suggestions — always visible */}
              <div className="space-y-2">
                <p className="text-xs dark:text-gray-500 text-textSecondary font-medium">
                  {promptTitle || promptDesc ? "Or try a classic:" : "Quick-start with a classic:"}
                </p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_PICKS.map((s) => (
                    <button
                      key={s.title}
                      type="button"
                      onClick={() => { setPromptTitle(s.title); setPromptDesc(s.desc); setPromptErrors({}); }}
                      className="px-3 py-1.5 rounded-full text-xs font-medium dark:bg-white/5 bg-gray-100 dark:text-gray-300 text-textSecondary border border-gray-200 dark:border-white/5 hover:border-blue-400/40 hover:text-blue-600 dark:hover:border-blue-400/30 dark:hover:text-blue-300 transition-all duration-150"
                    >
                      {s.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* API error — dismissible card */}
              {promptErrors.api && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20">
                  <div className="w-5 h-5 rounded-full bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-rose-500 text-xs font-bold">!</span>
                  </div>
                  <p className="text-sm text-rose-600 dark:text-rose-400 flex-1">{promptErrors.api}</p>
                  <button onClick={() => setPromptErrors({})} className="text-rose-400 hover:text-rose-600 dark:hover:text-rose-300 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* PHASE: Generating — animated step list */}
          {phase === PHASE.GENERATING && (
            <div className="flex flex-col items-center justify-center py-12 gap-6">
              {/* Animated icon */}
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15), rgba(236,72,153,0.1))" }}
              >
                <Sparkles size={28} className="text-blue-500 dark:text-blue-400 animate-pulse" />
              </div>

              {/* Step list */}
              <div className="w-full max-w-xs space-y-3">
                {GENERATE_STEPS.map((label, i) => {
                  const isDone = i < genStep;
                  const isActive = i === genStep;
                  return (
                    <div
                      key={label}
                      className={`flex items-center gap-3 transition-all duration-300 ${
                        isDone || isActive ? "opacity-100" : "opacity-30"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border transition-all duration-300 ${
                        isDone
                          ? "bg-[#069855]/10 border-[#069855] text-[#069855]"
                          : isActive
                          ? "border-blue-400 text-blue-400"
                          : "border-gray-300 dark:border-white/20 text-gray-400"
                      }`}>
                        {isDone ? (
                          <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="none">
                            <path d="M1.5 5l2.5 2.5L8.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : isActive ? (
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-ping" />
                        ) : (
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-white/20" />
                        )}
                      </div>
                      <span className={`text-sm ${
                        isDone
                          ? "text-[#069855] line-through opacity-60"
                          : isActive
                          ? "dark:text-white text-gray-800 font-medium"
                          : "text-gray-400 dark:text-gray-500"
                      }`}>{label}</span>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500">This takes about 10–15 seconds</p>
            </div>
          )}

          {/* PHASE 2: Review form */}
          {phase === PHASE.REVIEW && (
            <CodingProblemReviewForm
              formData={formData}
              setFormData={setFormData}
              errors={reviewErrors}
            />
          )}
        </div>

        {/* ── Footer ── */}
        {phase !== PHASE.GENERATING && (
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
            {phase === PHASE.REVIEW ? (
              <>
                <button
                  type="button"
                  onClick={() => { setPhase(PHASE.PROMPT); setReviewErrors({}); }}
                  className="border-btn dark:border-btn-dark flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                >
                  <ArrowLeft size={14} />
                  Edit Prompt
                </button>
                <button type="button" onClick={handleSave} className="button px-6 py-2.5">
                  <span>Save Problem</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="border-btn dark:border-btn-dark px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleGenerate}
                  className="button flex items-center gap-2 px-6 py-2.5"
                >
                  <Sparkles size={14} />
                  <span>Generate Problem</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HostCodingProblem;
